import { useEffect, useState } from "react";
import { Ruler, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { http, fail } from "@/adminApi";
import { money } from "@/pricing";
import { AddProductForm, PhotoUpload } from "@/AdminCatalog";
import { VariantEditor, toPayload } from "@/AdminVariants";

const FILTERS = ["published", "unpublished", "archived", "all"];
const toDraft = (variants) => variants.map((v) => ({ ...v, price_usd: String(v.price_usd), stock: String(v.stock) }));

function DeleteProduct({ product, onDone }) {
  const confirm = async () => {
    try {
      const r = await http.delete(`/admin/products/${product.id}`);
      onDone(r.data.action);
      toast.success(r.data.action === "deleted" ? `${product.name} deleted` : `${product.name} is in ${r.data.orders} past order(s), so it was archived instead`);
    } catch (e) {
      fail(e);
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="icon-action danger" aria-label="Delete product" data-testid={`admin-delete-${product.id}`}>
          <Trash2 size={15} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="admin-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {product.brand} {product.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            It will be removed from the store straight away. If it appears in any past order it will be archived instead, so those order records stay intact. Products that were never ordered are deleted permanently.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirm} data-testid="admin-delete-confirm">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function SizesPanel({ product, onSaved }) {
  const [draft, setDraft] = useState(toDraft(product.variants));
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await http.put(`/admin/products/${product.id}`, { variants: toPayload(draft) });
      onSaved(r.data);
      setDraft(toDraft(r.data.variants));
      toast.success(`Sizes saved for ${product.name}`);
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <tr className="sizes-row">
      <td colSpan={6}>
        <form onSubmit={save}>
          <VariantEditor value={draft} onChange={setDraft} testId={`admin-variants-${product.id}`} />
          <button className="btn btn-red admin-save" disabled={saving} data-testid={`admin-variants-save-${product.id}`}>
            {saving ? "Saving…" : "Save sizes"}
          </button>
        </form>
      </td>
    </tr>
  );
}

function ProductRow({ product, onSaved, onRemoved }) {
  const [open, setOpen] = useState(false);
  const update = async (changes, label) => {
    try {
      const r = await http.put(`/admin/products/${product.id}`, changes);
      onSaved(r.data);
      toast.success(`${product.name} ${label}`);
    } catch (e) {
      fail(e);
    }
  };
  const range = product.price_max_usd > product.price_usd ? `${money(product.price_usd)} – ${money(product.price_max_usd)}` : money(product.price_usd);
  return (
    <>
      <tr data-testid={`admin-product-row-${product.id}`}>
        <td>
          <PhotoUpload http={http} value={product.image} onChange={(image) => update({ image }, "photo updated")} testId={`admin-photo-${product.id}`} />
          <div>
            <b>{product.name}</b>
            <span>
              {product.brand} · {product.category === "tires" ? "tyre" : "alloy wheel (legacy)"}
            </span>
          </div>
        </td>
        <td>
          <b data-testid={`admin-price-range-${product.id}`}>{range}</b>
          <span>{product.variants.length} size(s)</span>
        </td>
        <td data-testid={`admin-stock-total-${product.id}`}>{product.stock}</td>
        <td>
          <select value={product.status} onChange={(e) => update({ status: e.target.value }, `set to ${e.target.value}`)} data-testid={`admin-status-${product.id}`}>
            <option value="published" disabled={product.category !== "tires"}>published</option>
            <option value="unpublished">unpublished</option>
            <option value="archived">archived</option>
          </select>
        </td>
        <td className="row-actions">
          {product.status === "published" && <button
            className={product.featured ? "icon-action featured" : "icon-action"}
            onClick={() => update({ featured: !product.featured }, product.featured ? "removed from homepage" : "featured on homepage")}
            aria-pressed={product.featured}
            data-testid={`admin-featured-${product.id}`}
          >
            <Star size={15} /> {product.featured ? "Featured" : "Feature"}
          </button>}
          <button className={open ? "icon-action active" : "icon-action"} onClick={() => setOpen(!open)} data-testid={`admin-sizes-toggle-${product.id}`}>
            <Ruler size={15} /> Sizes
          </button>
          <DeleteProduct product={product} onDone={(action) => (action === "deleted" ? onRemoved(product.id) : onSaved({ ...product, status: "archived" }))} />
        </td>
      </tr>
      {open && <SizesPanel product={product} onSaved={onSaved} />}
    </>
  );
}

export function ProductsTable() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("published");
  useEffect(() => {
    http.get("/admin/products").then((r) => setItems(r.data)).catch(fail);
  }, []);
  const onSaved = (p) => setItems((list) => list.map((x) => (x.id === p.id ? p : x)));
  const visible = items.filter((p) => filter === "all" || p.status === filter);
  return (
    <section className="admin-section" data-testid="admin-products">
      <div className="admin-section-head">
        <h2>
          Products <i>{items.length}</i>
        </h2>
        <AddProductForm http={http} onCreated={(p) => { setItems((list) => [p, ...list]); setFilter(p.status); }} />
      </div>
      <div className="admin-filters">
        {FILTERS.map((f) => (
          <button key={f} className={f === filter ? "active" : ""} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`}>
            {f} <i>{f === "all" ? items.length : items.filter((p) => p.status === f).length}</i>
          </button>
        ))}
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Price (USD)</th>
              <th>Total stock</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <ProductRow key={p.id} product={p} onSaved={onSaved} onRemoved={(id) => setItems((list) => list.filter((x) => x.id !== id))} />
            ))}
          </tbody>
        </table>
        {!visible.length && <p className="admin-empty">No {filter} products.</p>}
      </div>
    </section>
  );
}
